import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import DateField from "@/components/ui/date-field";
import DriverSelect from "@/components/ui/driver-select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { SubmitButton } from "@/components/ui/submit-button";
import { errorHandler } from "@/lib/utils";
import { driverVacationService } from "@/services/api/driverVacations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import * as z from "zod";

const formSchema = z
  .object({
    driver_id: z
      .number({
        error: "Por favor, selecione um motorista",
      })
      .min(1, "Por favor, selecione um motorista válido"),
    start_date: z.string({
      error: "Por favor, selecione a data de início",
    }),
    end_date: z.string({
      error: "Por favor, selecione a data de fim",
    }),
  })
  .refine(
    (data) => {
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);
      return endDate >= startDate;
    },
    {
      message: "A data de fim deve ser igual ou posterior à data de início",
      path: ["end_date"],
    },
  );

export type DriverVacationFormValues = z.infer<typeof formSchema>;

const DriverVacationsForm = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const isEditing = !!id;

  const form = useForm<DriverVacationFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      driver_id: 0,
      start_date: "",
      end_date: "",
    },
  });

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      driverVacationService
        .show(Number(id))
        .then((response) => {
          const vacation = response.data;
          form.setValue("driver_id", vacation.driver_id);
          form.setValue("start_date", vacation.start_date);
          form.setValue("end_date", vacation.end_date);
        })
        .catch((err) => {
          errorHandler(err, "Erro ao carregar dados das férias.");
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, form]);

  const onSubmit = async (values: DriverVacationFormValues) => {
    setIsLoading(true);
    try {
      if (isEditing) {
        await driverVacationService.update(Number(id), values);
        toast.success("Férias atualizadas com sucesso!");
      } else {
        await driverVacationService.create(values);
        toast.success("Férias cadastradas com sucesso!");
      }
      navigate("/driver-vacations");
    } catch (err) {
      errorHandler(err, "Erro ao salvar férias.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/driver-vacations");
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>
          {isEditing ? "Editar Férias" : "Cadastrar Férias"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="driver_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motorista</FormLabel>
                  <FormControl>
                    <DriverSelect
                      value={field.value}
                      onChange={(value) => field.onChange(Number(value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField form={form} name="start_date" label="Início" />

            <DateField form={form} name="end_date" label="Fim" />

            <div className="flex flex-col space-y-2 pt-4 border-t sm:flex-row sm:space-x-2 sm:space-y-0 justify-end">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
              <SubmitButton loading={isLoading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default DriverVacationsForm;
